"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useRouter } from "next/navigation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  useForm,
  useWatch,
  type FieldPath,
  type UseFormReturn,
} from "react-hook-form";
import { z } from "zod";
import {
  aboutStepSchema,
  organizationStepSchema,
  customizeStepSchema,
  employerStepSchema,
  projectStepSchema,
} from "../lib/onboarding-form-schema";
import {
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";
import {
  completeOnboarding,
  getGetOnboardingStatusQueryKey,
  getListEmployersQueryKey,
  saveStep1,
  saveStep2,
  saveStep3,
  saveStep4,
  saveStep5,
  useEnrichOrg,
  useGetOnboardingStatus,
  useListEmployers,
  type EnrichOrgQueryResult,
  type GetOnboardingStatusQueryResult,
  type SaveStep1MutationBody,
  type SaveStep2MutationBody,
  type SaveStep4MutationBody,
} from "@/lib/api/generated";
import {
  createDefaultEmployer,
  enrichEmployerProfile,
  type CreateDefaultEmployerMutationBody,
} from "@/lib/api/employer-profile";
import type { BodySaveStep3ApiOnboardingStep3Post } from "@/lib/api/model/bodySaveStep3ApiOnboardingStep3Post";
import { fetchPresetCoverAsFile } from "@/lib/image/covers";
import { ApiError } from "@/lib/api/error";
import { showToast } from "@/components/ui/sonner";
import { resetOnboardingCheck } from "@/components/layout/onboarding-guard";
import { useAuth } from "@/features/auth";
import { resetOrgCache } from "@/hooks/use-org";
import {
  ORGANIZATION_CONTEXT_SETUP_ERROR_MESSAGE,
  refreshOrganizationContext,
} from "@/lib/api/org-context";
import { getFullPhoneNumber } from "@/components/ui/phone-input";
import { detectBrowserTimezone } from "@/components/ui/timezone-select";
import { detectCountryCode, resolveCountryCode } from "@/lib/geo/detect-country";
import { FULL_ONBOARDING_STEPS } from "../components/wizard/constants";

const PROFILE_STEP = 1;
const ORGANIZATION_STEP = 2;
const PROJECT_STEP = 3;
const CUSTOMIZE_STEP = 4;
const PLAN_STEP = 5;

/** Backend returns "UTC" before step 1 is saved; prefer the browser timezone then. */
function resolveProfileTimezone(
  statusCurrentStep: number,
  serverTimezone: string | undefined,
): string {
  const browserTimezone = detectBrowserTimezone();
  const savedTimezone = serverTimezone?.trim();

  if (statusCurrentStep <= PROFILE_STEP) {
    if (!savedTimezone || savedTimezone === "UTC") return browserTimezone;
  }

  return savedTimezone || browserTimezone;
}

export interface OnboardingFormData {
  about: {
    avatar: string | null;
    first_name: string;
    last_name: string;
    phone_country_code: CountryCode;
    phone_number: string;
    timezone: string;
    email: string;
  };
  organization: {
    logo: string | null;
    name: string;
    website: string;
    size: string;
  };
  employer: {
    mission_vision: string;
    funding_traction: string;
    culture_principles: string;
    benefits: string;
  };
  project: {
    title: string;
    access_level: "shared" | "private";
    job_title: string;
    cover_file: File | null;
    cover_preview_url: string;
    created_id: string | null;
  };
  customize: {
    role: string[];
    priorities: string[];
    referral_source: string[];
  };
  invites: {
    draft_invite_email: string;
  };
}

type ApiData<T> = T extends { data: infer D } ? D : T;
type StatusResponse = ApiData<GetOnboardingStatusQueryResult>;
type EnrichResponse = ApiData<EnrichOrgQueryResult>;
type Step2SubStep = "org" | "employer";

function isEmployerRequiredError(err: unknown): err is ApiError {
  return err instanceof ApiError && err.code === "EMPLOYER_REQUIRED";
}

function shouldAutoDetectPhoneCountry(
  phoneNumber: string,
  status: StatusResponse | undefined,
): boolean {
  if (phoneNumber.trim()) return false;
  if (status?.phone?.trim()) return false;
  if (status && status.current_step > PROFILE_STEP) return false;
  return true;
}

function setFormErrorsFromZod(
  form: UseFormReturn<OnboardingFormData>,
  prefix: "about" | "organization" | "customize" | "project" | "employer",
  error: z.ZodError<unknown>,
) {
  for (const issue of error.issues) {
    const sub = issue.path.length ? issue.path.map(String).join(".") : "";
    const name = (
      sub ? `${prefix}.${sub}` : prefix
    ) as FieldPath<OnboardingFormData>;
    form.setError(name, { message: issue.message || "Invalid value" });
  }
}

export interface OnboardingWizardState {
  form: UseFormReturn<OnboardingFormData>;
  ready: boolean;
  currentStep: number;
  totalSteps: number;
  dir: number;
  creatingWorkspace: boolean;
  step2SubStep: Step2SubStep;
  enrichingEmployer: boolean;
  canContinue: boolean;
  submitting: boolean;
  next: () => Promise<void>;
  back: () => void;
}

export function useOnboardingWizard(): OnboardingWizardState {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { user, organization, isLoading: authLoading } = useAuth();
  const mounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );
  const [step, setStep] = useState<number | null>(null);
  const [dir, setDir] = useState(1);
  const [step2SubStep, setStep2SubStep] = useState<Step2SubStep>("org");
  const [enrichingEmployer, setEnrichingEmployer] = useState(false);

  const form = useForm<OnboardingFormData>({
    defaultValues: {
      about: {
        avatar: null,
        first_name: "",
        last_name: "",
        phone_country_code: "US" as CountryCode,
        phone_number: "",
        timezone: "",
        email: "",
      },
      organization: { logo: null, name: "", website: "", size: "1-10" },
      employer: {
        mission_vision: "",
        funding_traction: "",
        culture_principles: "",
        benefits: "",
      },
      project: {
        title: "",
        access_level: "shared",
        job_title: "",
        cover_file: null,
        cover_preview_url: "",
        created_id: null,
      },
      customize: { role: [], priorities: [], referral_source: [] },
      invites: { draft_invite_email: "" },
    },
  });

  const onboardingStatusQuery = useGetOnboardingStatus({
    query: {
      enabled: !authLoading && !!user,
      staleTime: 30_000,
      retry: 1,
    },
  });
  const status = onboardingStatusQuery.data as StatusResponse | undefined;
  const statusLoading = onboardingStatusQuery.isLoading;
  const statusError = onboardingStatusQuery.isError;

  const statusApplied = useRef(false);
  const countryDetected = useRef(false);
  useEffect(() => {
    if (!status || statusApplied.current) return;
    statusApplied.current = true;

    if (status.completed) {
      router.replace("/projects");
      return;
    }

    let phoneCountry = form.getValues("about.phone_country_code");
    let phoneNational = form.getValues("about.phone_number");
    if (status.phone) {
      const parsed = parsePhoneNumberFromString(status.phone);
      if (parsed) {
        phoneCountry = (parsed.country || phoneCountry) as CountryCode;
        phoneNational = parsed.formatNational();
      } else {
        phoneNational = status.phone;
      }
    }

    form.setValue(
      "about.first_name",
      status.first_name || user?.first_name || "",
    );
    form.setValue("about.last_name", status.last_name || user?.last_name || "");
    form.setValue("about.email", status.email || user?.email || "");
    form.setValue(
      "about.avatar",
      status.avatar_url || form.getValues("about.avatar"),
    );
    form.setValue("about.phone_country_code", phoneCountry);
    form.setValue("about.phone_number", phoneNational);
    const profileTimezone =
      form.getValues("about.timezone") ||
      resolveProfileTimezone(status.current_step, status.timezone);
    form.setValue("about.timezone", profileTimezone);
    if (shouldAutoDetectPhoneCountry(phoneNational, status)) {
      form.setValue(
        "about.phone_country_code",
        resolveCountryCode(profileTimezone),
      );
    }

    form.setValue(
      "organization.name",
      status.org_name || form.getValues("organization.name"),
    );
    form.setValue(
      "organization.website",
      status.org_website
        ? status.org_website.replace(/^https?:\/\//, "")
        : form.getValues("organization.website"),
    );
    form.setValue(
      "organization.size",
      status.org_size || form.getValues("organization.size"),
    );
    form.setValue(
      "organization.logo",
      status.org_logo_url || form.getValues("organization.logo"),
    );

    if (status.user_role_type)
      form.setValue("customize.role", [status.user_role_type]);
    if (status.talent_priorities)
      form.setValue("customize.priorities", status.talent_priorities);
    if (status.referral_source)
      form.setValue("customize.referral_source", [status.referral_source]);
  }, [status, user, router, form]);

  useEffect(() => {
    if (!statusError || !user) return;
    form.setValue("about.first_name", user.first_name || "");
    form.setValue("about.last_name", user.last_name || "");
    form.setValue("about.email", user.email || "");
    const currentTz = form.getValues("about.timezone");
    const profileTimezone = currentTz || detectBrowserTimezone();
    if (!currentTz) form.setValue("about.timezone", profileTimezone);
    if (shouldAutoDetectPhoneCountry(form.getValues("about.phone_number"), undefined)) {
      form.setValue("about.phone_country_code", resolveCountryCode(profileTimezone));
    }
  }, [statusError, user, form]);

  useEffect(() => {
    if (authLoading || !user || countryDetected.current) return;
    if (statusLoading && !statusError) return;

    countryDetected.current = true;

    const { phone_number: phoneNumber, timezone } = form.getValues("about");
    if (!shouldAutoDetectPhoneCountry(phoneNumber, status)) return;

    const profileTimezone =
      timezone ||
      resolveProfileTimezone(status?.current_step ?? PROFILE_STEP, status?.timezone);

    void detectCountryCode(profileTimezone).then((country) => {
      if (!shouldAutoDetectPhoneCountry(form.getValues("about.phone_number"), status)) {
        return;
      }
      form.setValue("about.phone_country_code", country);
    });
  }, [authLoading, user, statusLoading, statusError, status, form]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) router.replace("/login");
  }, [authLoading, user, router]);

  const enrichOrgQuery = useEnrichOrg({
    query: {
      enabled: !authLoading && !!user,
      staleTime: Infinity,
      retry: 0,
    },
  });
  const enrichment = enrichOrgQuery.data as EnrichResponse | undefined;

  const enrichApplied = useRef(false);
  useEffect(() => {
    if (!enrichment || enrichApplied.current) return;
    enrichApplied.current = true;

    const orgName = form.getValues("organization.name");
    const isDefault =
      !orgName.trim() ||
      orgName === "My Organization" ||
      orgName.endsWith("'s Organization");
    const enrichedName =
      enrichment.name === "My's Organization"
        ? "My Organization"
        : enrichment.name;
    if (isDefault && enrichedName)
      form.setValue("organization.name", enrichedName);
    if (isDefault && enrichment.domain)
      form.setValue("organization.website", enrichment.domain);
    if (!form.getValues("organization.logo") && enrichment.logo_url) {
      form.setValue("organization.logo", enrichment.logo_url);
    }
  }, [enrichment, form]);

  const enrichEmployerMutation = useMutation({
    mutationFn: () => enrichEmployerProfile(),
  });

  // Load any employer profile the user has already saved, so we (a) never
  // regenerate it (the enrich call is expensive and the backend currently
  // returns mostly-null content) and (b) never lose what they filled in across
  // a refresh or a Back/Continue. Only meaningful once the org exists.
  const employersQuery = useListEmployers(undefined, {
    query: {
      enabled: !authLoading && !!user && !!organization,
      staleTime: 30_000,
      retry: 0,
    },
  });
  const savedEmployer =
    employersQuery.data?.employers?.find((e) => e.is_default) ??
    employersQuery.data?.employers?.[0];
  const hasSavedEmployer =
    !!savedEmployer &&
    [
      savedEmployer.mission_vision,
      savedEmployer.funding_traction,
      savedEmployer.culture_principles,
      savedEmployer.benefits,
    ].some((v) => (v ?? "").trim() !== "");

  const saveStepMutation = useMutation({
    mutationFn: async ({
      s,
      step1Payload,
      step2Payload,
      preferencesPayload,
      projectPayload,
    }: {
      s: number;
      step1Payload: SaveStep1MutationBody;
      step2Payload: SaveStep2MutationBody;
      preferencesPayload: SaveStep4MutationBody;
      projectPayload: BodySaveStep3ApiOnboardingStep3Post | null;
    }) => {
      switch (s) {
        case PROFILE_STEP:
          return saveStep1(step1Payload);
        case ORGANIZATION_STEP: {
          const response = await saveStep2(step2Payload);
          if (organization === null) {
            if (!response.workos_org_id) {
              throw new Error(ORGANIZATION_CONTEXT_SETUP_ERROR_MESSAGE);
            }
            await refreshOrganizationContext(response.workos_org_id);
            await resetOrgCache(queryClient, router);
          }
          return response;
        }
        case PROJECT_STEP: {
          if (!projectPayload) throw new Error("Project payload missing");
          const created = await saveStep3(projectPayload);
          form.setValue("project.created_id", created.id);
          return created;
        }
        case CUSTOMIZE_STEP:
          return saveStep4(preferencesPayload);
        case PLAN_STEP:
          return saveStep5();
        default:
          throw new Error("Invalid onboarding step.");
      }
    },
    onSuccess: (_data, { s }) => {
      // A remount inside staleTime must not reopen a saved step. Skips the org
      // save: without has_default_employer (older API) pointer 3 skips the sub-step.
      if (s < PROJECT_STEP) return;
      queryClient.setQueryData(
        getGetOnboardingStatusQueryKey(),
        (prev: StatusResponse | undefined) =>
          prev
            ? {
                ...prev,
                current_step: Math.max(
                  prev.current_step,
                  Math.min(s + 1, PLAN_STEP),
                ),
              }
            : prev,
      );
    },
    onError: (err: unknown) => {
      if (isEmployerRequiredError(err)) {
        showToast.warning("Finish your company profile", {
          description: err.message,
        });
        return;
      }
      const message =
        err instanceof Error ? err.message : "Something went wrong.";
      showToast.error("Something went wrong", { description: message });
    },
  });

  const createEmployerMutation = useMutation({
    mutationFn: (data: CreateDefaultEmployerMutationBody) =>
      createDefaultEmployer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListEmployersQueryKey() });
      queryClient.setQueryData(
        getGetOnboardingStatusQueryKey(),
        (prev: StatusResponse | undefined) =>
          prev
            ? {
                ...prev,
                current_step: Math.max(prev.current_step, PROJECT_STEP),
                has_default_employer: true,
              }
            : prev,
      );
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error ? err.message : "Something went wrong.";
      showToast.error("Could not save employer profile", {
        description: message,
      });
    },
  });

  const completeMutation = useMutation({
    mutationFn: () => completeOnboarding(),
    onSuccess: async () => {
      queryClient.setQueryData(
        getGetOnboardingStatusQueryKey(),
        (prev: StatusResponse | undefined) =>
          prev
            ? { ...prev, completed: true }
            : ({ completed: true } as StatusResponse),
      );
      resetOnboardingCheck();
      await queryClient.invalidateQueries({ queryKey: ["auth"] });
      router.push("/talent-search?welcome=true");
    },
    onError: () =>
      showToast.error("Onboarding failed", {
        description: "Failed to complete onboarding.",
      }),
  });

  const totalSteps =
    status?.onboarding_mode === "profile_only"
      ? PROFILE_STEP
      : FULL_ONBOARDING_STEPS;
  // The org save moves the server to step 3 before the employer sub-step
  // posts, so resume the sub-step until the backend reports a default employer
  // (step 3 is rejected without one).
  const resumeEmployerSubStep =
    !!status &&
    !status.completed &&
    status.onboarding_mode !== "profile_only" &&
    status.current_step === PROJECT_STEP &&
    status.has_default_employer === false;
  const currentStep =
    step ??
    (resumeEmployerSubStep
      ? ORGANIZATION_STEP
      : status
        ? Math.min(Math.max(status.current_step, PROFILE_STEP), totalSteps)
        : PROFILE_STEP);
  const activeSubStep: Step2SubStep =
    step === null && resumeEmployerSubStep ? "employer" : step2SubStep;
  const creatingWorkspace =
    currentStep === ORGANIZATION_STEP && organization === null;

  // Hydrate the form from a previously-saved employer profile (once). If the
  // user already completed it and lands back on step 2, resume the employer
  // sub-step instead of the org form.
  const employerApplied = useRef(false);
  useEffect(() => {
    if (!hasSavedEmployer || !savedEmployer || employerApplied.current) return;
    employerApplied.current = true;

    form.setValue("employer.mission_vision", savedEmployer.mission_vision ?? "");
    form.setValue(
      "employer.funding_traction",
      savedEmployer.funding_traction ?? "",
    );
    form.setValue(
      "employer.culture_principles",
      savedEmployer.culture_principles ?? "",
    );
    form.setValue("employer.benefits", savedEmployer.benefits ?? "");

    // eslint-disable-next-line react-hooks/set-state-in-effect -- seed the sub-step once from externally-loaded server data (documented escape hatch in AGENTS.md); afterwards it is user-driven via next()/back().
    if (currentStep === ORGANIZATION_STEP) setStep2SubStep("employer");
  }, [hasSavedEmployer, savedEmployer, currentStep, form]);

  // Refs, not state: next() can hold a stale closure while a research call is
  // in flight, and a second call would overwrite what the user typed since.
  const employerEnrichmentInFlight = useRef(false);
  const employerEnrichmentStarted = useRef(false);
  const startEmployerEnrichment = useCallback(
    (employer: OnboardingFormData["employer"]) => {
      if (employerEnrichmentInFlight.current) return;
      const employerAlreadyFilled =
        hasSavedEmployer ||
        [
          employer.mission_vision,
          employer.funding_traction,
          employer.culture_principles,
          employer.benefits,
        ].some((v) => (v ?? "").trim() !== "");
      if (employerAlreadyFilled) return;

      employerEnrichmentInFlight.current = true;
      employerEnrichmentStarted.current = true;
      setEnrichingEmployer(true);
      enrichEmployerMutation
        .mutateAsync()
        .then((enrichment) => {
          form.setValue(
            "employer.mission_vision",
            enrichment.mission_vision ?? "",
          );
          form.setValue(
            "employer.funding_traction",
            enrichment.funding_traction ?? "",
          );
          form.setValue(
            "employer.culture_principles",
            enrichment.culture_principles ?? "",
          );
          form.setValue("employer.benefits", enrichment.benefits ?? "");
        })
        .catch(() => {})
        .finally(() => {
          employerEnrichmentInFlight.current = false;
          setEnrichingEmployer(false);
        });
    },
    [hasSavedEmployer, enrichEmployerMutation, form],
  );

  // A resumed sub-step skipped the org save that normally starts the research.
  // Wait for the saved-employer lookup so existing content isn't regenerated,
  // and skip it when this mount already researched (a refetch mid-flow).
  const resumeEnrichmentChecked = useRef(false);
  const employersSettled = !employersQuery.isLoading;
  useEffect(() => {
    if (!resumeEmployerSubStep || step !== null || !employersSettled) return;
    if (resumeEnrichmentChecked.current) return;
    resumeEnrichmentChecked.current = true;
    if (employerEnrichmentStarted.current) return;
    startEmployerEnrichment(form.getValues("employer"));
  }, [
    resumeEmployerSubStep,
    step,
    employersSettled,
    startEmployerEnrichment,
    form,
  ]);

  const next = useCallback(async () => {
    const values = form.getValues();

    if (currentStep === PROFILE_STEP) {
      form.clearErrors([
        "about.first_name",
        "about.last_name",
        "about.phone_number",
        "about.timezone",
      ] as FieldPath<OnboardingFormData>[]);
      const step1check = aboutStepSchema.safeParse({
        first_name: values.about.first_name,
        last_name: values.about.last_name,
        phone_number: values.about.phone_number,
        phone_country_code: values.about.phone_country_code,
        timezone: values.about.timezone,
      });
      if (!step1check.success) {
        setFormErrorsFromZod(form, "about", step1check.error);
        return;
      }
    } else if (currentStep === ORGANIZATION_STEP) {
      if (activeSubStep === "org") {
        form.clearErrors([
          "organization.name",
          "organization.website",
          "organization.size",
        ] as FieldPath<OnboardingFormData>[]);
        const step2check = organizationStepSchema.safeParse(values.organization);
        if (!step2check.success) {
          setFormErrorsFromZod(form, "organization", step2check.error);
          return;
        }
      } else {
        const trimOrNull = (v: string) => {
          const t = v?.trim();
          return t || null;
        };
        try {
          await createEmployerMutation.mutateAsync({
            mission_vision: trimOrNull(values.employer.mission_vision),
            funding_traction: trimOrNull(values.employer.funding_traction),
            culture_principles: trimOrNull(values.employer.culture_principles),
            benefits: trimOrNull(values.employer.benefits),
          });
        } catch {
          return;
        }
        setStep2SubStep("org");
        setDir(1);
        setStep(currentStep + 1);
        return;
      }
    } else if (currentStep === PROJECT_STEP) {
      form.clearErrors([
        "project.title",
        "project.access_level",
        "project.job_title",
      ] as FieldPath<OnboardingFormData>[]);
      const projectCheck = projectStepSchema.safeParse({
        title: values.project.title,
        access_level: values.project.access_level,
        job_title: values.project.job_title,
      });
      if (!projectCheck.success) {
        setFormErrorsFromZod(form, "project", projectCheck.error);
        return;
      }
    } else if (currentStep === CUSTOMIZE_STEP) {
      form.clearErrors([
        "customize.role",
        "customize.priorities",
        "customize.referral_source",
      ] as FieldPath<OnboardingFormData>[]);
      const step3check = customizeStepSchema.safeParse(values.customize);
      if (!step3check.success) {
        setFormErrorsFromZod(form, "customize", step3check.error);
        return;
      }
    }

    const step1Payload: SaveStep1MutationBody = {
      first_name: values.about.first_name,
      last_name: values.about.last_name,
      phone: getFullPhoneNumber(
        values.about.phone_country_code,
        values.about.phone_number,
      ),
      timezone: values.about.timezone,
      avatar_url: values.about.avatar,
    };
    const step2Payload: SaveStep2MutationBody = {
      org_name: values.organization.name,
      org_website: values.organization.website
        ? `https://${values.organization.website}`
        : "",
      org_size: values.organization.size as SaveStep2MutationBody["org_size"],
      org_logo_url: values.organization.logo,
    };
    const preferencesPayload: SaveStep4MutationBody = {
      user_role_type: values.customize.role[0] || "",
      talent_priorities: values.customize.priorities,
      referral_source: values.customize.referral_source[0] || "",
    };

    let projectPayload: BodySaveStep3ApiOnboardingStep3Post | null = null;
    if (currentStep === PROJECT_STEP && !values.project.created_id) {
      let coverFile = values.project.cover_file;
      const previewUrl = values.project.cover_preview_url;
      if (!coverFile && previewUrl && !previewUrl.startsWith("blob:")) {
        try {
          coverFile = await fetchPresetCoverAsFile(previewUrl);
          form.setValue("project.cover_file", coverFile, { shouldDirty: true });
        } catch {}
      }
      projectPayload = {
        title: values.project.title.trim(),
        access_level: values.project.access_level,
        job_title: values.project.job_title.trim(),
        cover: coverFile ?? undefined,
      };
    }

    const alreadyCreatedThisStep =
      currentStep === PROJECT_STEP && !!values.project.created_id;

    if (currentStep <= totalSteps && !alreadyCreatedThisStep) {
      try {
        await saveStepMutation.mutateAsync({
          s: currentStep,
          step1Payload,
          step2Payload,
          preferencesPayload,
          projectPayload,
        });
      } catch (err) {
        if (isEmployerRequiredError(err)) {
          setDir(-1);
          setStep(ORGANIZATION_STEP);
          setStep2SubStep("employer");
          startEmployerEnrichment(values.employer);
        }
        return;
      }
    }

    if (currentStep === ORGANIZATION_STEP && activeSubStep === "org") {
      setStep2SubStep("employer");
      setDir(1);
      startEmployerEnrichment(values.employer);
      return;
    }

    if (currentStep < totalSteps) {
      setDir(1);
      setStep(currentStep + 1);
    } else {
      completeMutation.mutate();
    }
  }, [
    currentStep,
    activeSubStep,
    form,
    saveStepMutation,
    startEmployerEnrichment,
    createEmployerMutation,
    completeMutation,
    totalSteps,
  ]);

  const back = useCallback(() => {
    if (currentStep === ORGANIZATION_STEP && activeSubStep === "employer") {
      setDir(-1);
      setStep(ORGANIZATION_STEP);
      setStep2SubStep("org");
      form.clearErrors();
      return;
    }
    if (currentStep > PROFILE_STEP) {
      setDir(-1);
      setStep(currentStep - 1);
      form.clearErrors();
    }
  }, [currentStep, activeSubStep, form]);

  const firstName =
    useWatch({ control: form.control, name: "about.first_name" }) ?? "";
  const lastName =
    useWatch({ control: form.control, name: "about.last_name" }) ?? "";
  const watchedTimezone =
    useWatch({ control: form.control, name: "about.timezone" }) ?? "";
  const watchedOrgName =
    useWatch({ control: form.control, name: "organization.name" }) ?? "";
  const roles =
    useWatch({ control: form.control, name: "customize.role" }) ?? [];
  const priorities =
    useWatch({ control: form.control, name: "customize.priorities" }) ?? [];
  const referralSources =
    useWatch({ control: form.control, name: "customize.referral_source" }) ??
    [];
  const projectTitle =
    useWatch({ control: form.control, name: "project.title" }) ?? "";
  const projectJobTitle =
    useWatch({ control: form.control, name: "project.job_title" }) ?? "";
  const employerMission =
    useWatch({ control: form.control, name: "employer.mission_vision" }) ?? "";
  const employerFunding =
    useWatch({ control: form.control, name: "employer.funding_traction" }) ??
    "";
  const employerCulture =
    useWatch({ control: form.control, name: "employer.culture_principles" }) ??
    "";
  const employerBenefits =
    useWatch({ control: form.control, name: "employer.benefits" }) ?? "";

  const canContinue = (() => {
    switch (currentStep) {
      case PROFILE_STEP:
        return (
          firstName.trim() !== "" &&
          lastName.trim() !== "" &&
          watchedTimezone.trim() !== ""
        );
      case ORGANIZATION_STEP:
        if (activeSubStep === "employer") {
          return true;
        }
        return watchedOrgName.trim() !== "";
      case PROJECT_STEP:
        return projectTitle.trim() !== "" && projectJobTitle.trim() !== "";
      case CUSTOMIZE_STEP:
        return (
          roles.length > 0 &&
          priorities.length > 0 &&
          referralSources.length > 0
        );
      case PLAN_STEP:
        return true;
      default:
        return true;
    }
  })();

  const submitting =
    saveStepMutation.isPending ||
    enrichEmployerMutation.isPending ||
    createEmployerMutation.isPending ||
    completeMutation.isPending;
  const ready = !!mounted && !authLoading && !statusLoading;

  return {
    form,
    ready,
    currentStep,
    totalSteps,
    dir,
    creatingWorkspace,
    step2SubStep: activeSubStep,
    enrichingEmployer,
    canContinue,
    submitting,
    next,
    back,
  };
}
